import React from "react";
import type { User as UserAuth } from "next-auth";

import { IS_DUB_REFERRALS_ENABLED } from "@calcom/lib/constants";
import { useHasActiveTeamPlan } from "@calcom/lib/hooks/useHasPaidPlan";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import { trpc } from "@calcom/trpc/react";
import { showToast } from "@calcom/ui/components/toast";

import { type NavigationItemType } from "./navigation/NavigationItem";

type BottomNavItemsProps = {
  publicPageUrl: string;
  isAdmin: boolean;
  user: UserAuth | null | undefined;
};

export function useBottomNavItems({
  publicPageUrl,
  isAdmin,
  user,
}: BottomNavItemsProps): NavigationItemType[] {
  const { t } = useLocale();
  const { isTrial } = useHasActiveTeamPlan();
  const utils = trpc.useUtils();

  const skipTeamTrialsMutation = trpc.viewer.teams.skipTeamTrials.useMutation({
    onSuccess: () => {
      utils.viewer.teams.hasActiveTeamPlan.invalidate();
      showToast(t("team_trials_skipped_successfully"), "success");
    },
    onError: () => {
      showToast(t("something_went_wrong"), "error");
    },
  });

  // Check if user is a platform practitioner with bookingEngineEnabled=false
  const isPlatformPractitioner = user?.userCategory === "PRACTITIONER" && user?.userType === "PLATFORM";
  const shouldHideItems = isPlatformPractitioner && user?.bookingEngineEnabled === false;

  return [
    // Render above to prevent layout shift as much as possible
    isTrial
      ? {
          name: "skip_trial",
          href: "",
          isLoading: skipTeamTrialsMutation.isPending,
          icon: "clock",
          onClick: (e: { preventDefault: () => void }) => {
            e.preventDefault();
            skipTeamTrialsMutation.mutate({});
          },
        }
      : null,
    // {
    //   name: "view_public_page",
    //   href: publicPageUrl,
    //   icon: "external-link",
    //   target: "__blank",
    // },
    shouldHideItems ? null : {
      name: "My Raiqa",
      href: '/my-raiqa',
      icon: "user",
    },
    shouldHideItems ? null : {
      name: "view_public_page",
      href: publicPageUrl,
      icon: "external-link",
      target: "__blank",
    },
    // {
    //   name: "Members",
    //   href: "https://app.medirecords.com",
    //   customIconElement: React.createElement("img", {
    //     src: "/images/mr-logo.png",
    //     alt: "MediRecords",
    //     width: 32,  // Increased to 32px
    //     height: 32, // Increased to 32px
    //     className: "flex-shrink-0"
    //   }),
    //   target: "_blank",
    // },
    // {
    //   name: "copy_public_page_link",
    //   href: "",
    //   onClick: (e: { preventDefault: () => void }) => {
    //     e.preventDefault();
    //     navigator.clipboard.writeText(publicPageUrl);
    //     showToast(t("link_copied"), "success");
    //   },
    //   icon: "copy",
    // },
    IS_DUB_REFERRALS_ENABLED
      ? {
          name: "referral_text",
          href: "/refer",
          icon: "gift",
        }
      : null,

    isAdmin
      ? {
          name: "impersonation",
          href: "/settings/admin/impersonation",
          icon: "lock",
        }
      : null,
    {
      name: "settings",
      href: user?.org ? `/settings/organizations/profile` : "/settings/my-account/profile",
      icon: "settings",
    },
  ].filter(Boolean) as NavigationItemType[];
}
